import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const RANGES = [7, 30];

export function RevenueChart() {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get("/admin/revenue/daily", { params: { days } }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not load revenue"));
  }, [days]);
  const data = (d?.series || []).map((r) => ({ ...r, label: r.date.slice(5).split("-").reverse().join("/") }));

  return (
    <Card className="admin-card p-5 mt-5" data-testid="revenue-chart-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Live payments · Razorpay</div>
          <div className="font-bold text-lg mt-0.5">Daily revenue</div>
        </div>
        <div className="flex rounded-lg border border-slate-700 overflow-hidden" data-testid="revenue-range">
          {RANGES.map((n) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`revenue-range-${n}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${days === n ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-400 hover:text-slate-100"}`}>{n} days</button>
          ))}
        </div>
      </div>
      {d && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
          <Stat label="Revenue" value={inr(d.totals.revenue)} tone="text-orange-400" testid="revenue-total"/>
          <Stat label="Refunds" value={inr(d.totals.refunds)} tone="text-sky-400" testid="revenue-refunds"/>
          <Stat label="Net" value={inr(d.totals.net)} tone="text-emerald-400" testid="revenue-net"/>
          <Stat label="Failed payments" value={d.totals.failed} tone="text-red-400" testid="revenue-failed"/>
          {!d.totals.revenue && !d.totals.refunds && !d.totals.failed &&
            <div className="col-span-2 sm:col-span-4 text-xs text-slate-500" data-testid="revenue-empty">No live payments in this period yet.</div>}
        </div>
      )}
      <div className="h-64 mt-4" data-testid="revenue-chart">
        {err ? <div className="text-sm text-red-300">{err}</div> : !d ? <div className="text-sm text-slate-500">Loading…</div> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1e293b" vertical={false}/>
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd"/>
              <YAxis yAxisId="amt" tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `₹${v}`}/>
              <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={28}/>
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                formatter={(v, name) => (name === "Failed" ? v : inr(v))}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              <Bar yAxisId="amt" dataKey="revenue" name="Revenue" fill="#f97316" radius={[4, 4, 0, 0]}/>
              <Bar yAxisId="amt" dataKey="refunds" name="Refunds" fill="#38bdf8" radius={[4, 4, 0, 0]}/>
              <Line yAxisId="cnt" type="monotone" dataKey="failed" name="Failed" stroke="#f87171" strokeWidth={2} dot={{ r: 2 }}/>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-lg border border-slate-800 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-bold text-lg ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}
